import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { AutoCoderPipelineRecord } from '../../../types/gotypes'

interface DataPreviewTableProps {
  records: AutoCoderPipelineRecord[]
  totalCount: number
  page: number
  rowsPerPage: number
  isLoading?: boolean
  error?: string
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
}

const DataPreviewTable: React.FC<DataPreviewTableProps> = ({
  records,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  error,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const handlePageChange = (_event: unknown, newPage: number) => {
    onPageChange(newPage)
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10))
    onPageChange(0)
  }

  return (
    <Paper elevation={1} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          Data Preview ({totalCount} records)
        </Typography>
      </Box>

      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100, fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Source Text</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Alert severity="error">{error}</Alert>
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records loaded
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell sx={{ width: 100, verticalAlign: 'top' }}>
                    {record.interview_id}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {record.data?.source_text || '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
      />
    </Paper>
  )
}

export default DataPreviewTable
